import { useQuery } from "@tanstack/react-query";
import {
  useCallback,
  // useEffect,
  useRef,
  useState
} from "react";
import { useCookies } from "react-cookie";
import { toast } from "react-toastify";

import { ExcelFilePreview } from "./ExcelFilePreview";
import styles from "./ImportConfigurationPage.module.scss";

import {
  // getDefaultImportConfiguration,
  getDestinationMetadata,
  getImportConfigurations,
  deleteImportConfiguration
} from "../../api/template/template";
import { ReactComponent as ArrowBack } from "../../assets/icons/arrowBack-icon.svg";
import { useClickOutside } from "../../hooks/useClickOutside";
import { useUserData } from "../../hooks/useUserProfile";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import {
  selectSliceImportConfigurations,
  selectSliceSelectedImportConfiguration,
  setSliceImportConfigurations,
  setSliceSelectedImportConfiguration
} from "../../store/slices/importConfigurationSlice";
// import { selectParseResult } from "../../store/slices/parseResultSlice";
import {
  setTemplateSPage,
  setTemplateStep,
} from "../../store/slices/stepSlice";
import { selectDemo, setDemo } from "../../store/slices/uploadFileSlice";
// import { EntityMetadata } from "../../types/entityMetadata";
import {
  ImportConfiguration,
  // ImportConfigurationArgument
} from "../../types/importConfiguration";
import { ETemplatePage, ETemplateStep } from "../../types/steps";
import { createErrorMessage } from "../../utils/createErrorMessage";
import { Auth } from "../Auth";
import { CustomButton } from "../FormFields/CustomButton";
// import {
// //   // CustomColumnsType,
//   SelectOption
// } from '../FormFields/CustomTable';
import { ReactPortal } from "../ReactPortal";
import { ReactPortalSubstrate } from "../ReactPortalSubstrate";
import { Title } from "../Title";

// const mapToSelectOptions = (metadata: EntityMetadata[]): SelectOption[] => {
//   return metadata.map(item => ({
//     label: item.displayName,
//     value: item.identifier,
//   }));
// };

export const ImportConfigurationPage = () => {
  const dispatch = useAppDispatch();
  const adapterIdentifier = "ExcelAdapter";
  const { organizationId, tenantId } = useUserData();

  const isDemo = useAppSelector(selectDemo);
  const importConfigurations = useAppSelector(selectSliceImportConfigurations) ?? [];
  const selectedImportConfiguration = useAppSelector(selectSliceSelectedImportConfiguration);
  // const parseResult = useAppSelector(selectParseResult);

  const setSelectedImportConfiguration = useCallback((config: ImportConfiguration | null) => {
    dispatch(setSliceSelectedImportConfiguration(config));
  }, [dispatch, setSliceSelectedImportConfiguration])

  // const sourceTables = mapToSelectOptions(parseResult.entityMetadata);
  // const sourceTables = [];

  // Fetch import configurations
  const { refetch: refetchConfigurations } = useQuery({
    queryKey: ["getImportConfigurations", tenantId, organizationId, adapterIdentifier],
    queryFn: async () => {
      try {
        const res = await getImportConfigurations(organizationId,
          tenantId,
          adapterIdentifier);

        // res.data.data.forEach((importConfiguration: ImportConfiguration) => {
        //   const isAvailable = sourceTables.some(
        //     (available) => available.value === importConfiguration.sourceIdentifier
        //   );

        //   if (!isAvailable) {
        //     importConfiguration.sourceIdentifier = null;
        //   }
        // });

        dispatch(setSliceImportConfigurations(res.data.data));
        return res.data.data;
      } catch (error) {
        toast.error(createErrorMessage(error));
      }
    },
    enabled: !!tenantId
  });

  // Fetch field mappings
  useQuery({
    queryKey: ["getDestinationMetadata"],
    queryFn: async () => {
      try {
        const res = await getDestinationMetadata();
        return res.data.data;
      } catch (error) {
        toast.error(createErrorMessage(error));
      }
    },
    enabled: !!tenantId
  });

  const [isPortalOpen, seIsPortalOpen] = useState(false);
  const [token] = useCookies(["accessToken"]);


  const formRef = useRef<HTMLDivElement>(null);

  const onBackClick = () => {
    dispatch(setSliceImportConfigurations(importConfigurations));
    dispatch(setSliceSelectedImportConfiguration(selectedImportConfiguration));

    dispatch(setTemplateSPage(ETemplatePage.SelectTemplatePage));
    dispatch(setTemplateStep(ETemplateStep.step2));
    dispatch(setDemo(null));
  };

  const onNextClick = () => {

    dispatch(setSliceImportConfigurations(importConfigurations));
    dispatch(setSliceSelectedImportConfiguration(selectedImportConfiguration));

    dispatch(setTemplateSPage(ETemplatePage.ImportConfigurationFieldMappingPage));
    dispatch(setTemplateStep(ETemplateStep.step4));
  };

  const isDisable = () => {
    if (!selectedImportConfiguration || !selectedImportConfiguration.sourceIdentifier) {
      return true;
    }

    return false;
  };

  const onOpenClick = () => {
    seIsPortalOpen(true);
  };

  const onCloseClick = () => {
    seIsPortalOpen(false);
  };

  useClickOutside(formRef, onCloseClick);

  const handleDataSourceImportConfigurationChange = (newDataSource: ImportConfiguration[]) => {
    dispatch(setSliceImportConfigurations(newDataSource));
  };

  const handleConfigurationDelete = async (config: ImportConfiguration) => {
    try {
      await deleteImportConfiguration(organizationId, tenantId, config.id);
      toast.success("Configuration deleted successfully");
      refetchConfigurations();
    } catch (error) {
      toast.error(createErrorMessage(error));
    }
  };

  return (
    <div className={styles.root}>
      {token.accessToken && isDemo !== "demo" && (
        <Title tagName={"h3"} className={styles.title}>
          Select import configuration for uploaded file
        </Title>
      )}

      <ExcelFilePreview
        importConfigurations={importConfigurations}
        selectedImportConfiguration={selectedImportConfiguration}
        setSelectedImportConfiguration={setSelectedImportConfiguration}
        handleDataSourceImportConfigurationChange={handleDataSourceImportConfigurationChange}
        handleConfigurationDelete={handleConfigurationDelete}
      />
      <div className={styles.wrapperBtn}>
        <CustomButton
          theme={"secondary"}
          className={styles.btn}
          onClick={onBackClick}
        >
          <ArrowBack className={styles.icon} />
          Back
        </CustomButton>
        <CustomButton
          isDisabled={isDisable()}
          className={styles.btn}
          onClick={onNextClick}
        >
          Next
        </CustomButton>
      </div>

      {!token.accessToken && (
        <ReactPortal>
          <div className={styles.footerLogin}>
            <CustomButton className={styles.loginBtn} onClick={onOpenClick}>
              Login to continue
            </CustomButton>
          </div>
        </ReactPortal>
      )}
      {isPortalOpen && (
        <ReactPortal>
          <ReactPortalSubstrate className={styles.portalWrapper}>
            <Auth ref={formRef} onClose={onCloseClick} />
          </ReactPortalSubstrate>
        </ReactPortal>
      )}
    </div>
  );
};